import type { GameBetSummaryRow } from '@/types';

/**
 * CSV export of the bets exactly as queried — raw values, not the formatted ones
 * on screen. Amounts keep full precision (they are strings end to end).
 *
 * `currency` sits immediately beside each amount because these rows are NOT
 * converted: a mixed-currency export whose numbers look comparable is a trap,
 * and a spreadsheet SUM() over the bet column would be meaningless without it.
 */
export function toCsv(rows: GameBetSummaryRow[]): string {
  const header = [
    'combined_round_id',
    'game_uuid',
    'bet_id',
    'round_id',
    'player_id',
    'currency',
    'bet',
    'win',
    'rollback',
    'ggr',
    'transactions',
    'rolled_back',
    'started_at_utc',
    'ended_at_utc',
  ];
  const lines = rows.map((r) =>
    [
      r.combined_round_id,
      r.game_uuid,
      r.bet_id,
      r.round_id,
      r.player_id,
      r.currency,
      r.bet,
      r.win,
      r.rollback_amount,
      r.ggr,
      String(r.transactions),
      String(r.rolled_back),
      r.started_at,
      r.ended_at,
    ].map(escapeCell),
  );
  return [header.join(','), ...lines.map((l) => l.join(','))].join('\n');
}

/** RFC-4180 quoting: wrap when the value could break the row, double inner quotes. */
function escapeCell(value: string): string {
  const v = value ?? '';
  return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

export function downloadCsv(filename: string, csv: string): void {
  // BOM so Excel opens UTF-8 correctly.
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Ticket-friendly filename from the CLIENT and GAME names, not their ids — a
 * pair of opaque hashes tells nobody what the export is of. Sanitised so a name
 * with spaces or punctuation can't inject a path.
 */
export function gameReportFilename(client: string, game: string, from: string): string {
  const safe = (s: string) => s.replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 40);
  return `game-report_${safe(client)}_${safe(game)}_${safe(from.slice(0, 10))}.csv`;
}
